import React from 'react';
import Link from 'next/link';
import { ChevronRight, Home } from 'lucide-react';
import JsonLd from './JsonLd';

/**
 * Premium Breadcrumb component for Royal Prime Labels.
 * Handles both visual navigation and SEO structured data.
 * 
 * @param {Array} items - Array of breadcrumb items [{ name: string, url: string }]
 * @param {string} className - Optional additional CSS classes
 */
const Breadcrumbs = ({ items, className = "" }) => {
  // Always prepend Home if not already present
  const breadcrumbs = items[0]?.url === '/' 
    ? items 
    : [{ name: 'Home', url: '/' }, ...items];

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`w-full pt-[72px] sm:pt-[80px] lg:pt-[100px] py-4 border-b border-slate-100 bg-white/50 backdrop-blur-sm z-40 ${className}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12">
        <JsonLd type="BreadcrumbList" data={breadcrumbs} />
        <ol className="flex items-center flex-wrap gap-y-2">
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            return (
              <li key={index} className="flex items-center min-w-0">
                {index !== 0 && (
                  <ChevronRight className="w-4 h-4 mx-2 sm:mx-3 text-slate-300 shrink-0" />
                )}
                
                {isLast ? (
                  <span 
                    className="font-bold text-slate-900 truncate"
                    aria-current="page"
                  >
                    {item.name}
                  </span>
                ) : (
                  <Link
                    href={item.url}
                    className="text-slate-500 hover:text-blue-700 transition-all duration-300 flex items-center gap-1.5 group"
                  >
                    {index === 0 && (
                      <Home className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                    )}
                    <span className="text-sm font-medium tracking-wide uppercase truncate">
                      {item.name}
                    </span>
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
